/**
 * Tints: colour as identity, not as state.
 *
 * Every metric on the dashboard gets its own hue — indigo for population,
 * green for engagement, magenta for money, amber for volume, red for the
 * backlog. The hue travels with the measure: the same magenta is on the
 * revenue tile's icon, its sparkline, and the revenue line on the chart below.
 * That is what lets someone scan a row of six cards and know which is which
 * before reading a single label.
 *
 * This is deliberately a *separate* vocabulary from `status-tone.ts`. A tone
 * says "this thing is failing"; a tint says "this thing is revenue". Mixing
 * them is how a dashboard ends up with six cards that look like six different
 * alert levels — so a tint is never chosen from a value, only from what the
 * measure *is*.
 *
 * Kept out of the component files so that adding a tint does not force a
 * refresh of every module that renders one, and so Vite's fast refresh is not
 * broken by a module exporting both a component and a lookup table.
 */

export type Tint = "brand" | "success" | "accent" | "warning" | "danger" | "info";

/** The wash behind an icon tile. */
export const TINT_SOFT: Record<Tint, string> = {
  brand: "bg-brand-soft",
  success: "bg-success-soft",
  accent: "bg-accent-soft",
  warning: "bg-warning-soft",
  danger: "bg-danger-soft",
  info: "bg-info-soft",
};

/** The glyph on that wash. Every pair clears contrast in both themes. */
export const TINT_INK: Record<Tint, string> = {
  brand: "text-brand",
  success: "text-success",
  accent: "text-accent",
  warning: "text-warning",
  danger: "text-danger",
  info: "text-info",
};

/**
 * The raw colour behind each tint, for marks that are drawn rather than
 * classed — an SVG stroke cannot take a Tailwind class.
 *
 * This is what a tile's sparkline uses, so the line is the *same* colour as
 * the icon above it rather than an approximation from the categorical palette.
 * It also means `danger` gets its own red instead of borrowing the magenta:
 * there is no fifth-versus-sixth-slot problem when a chart has one series.
 */
export const TINT_COLOR: Record<Tint, string> = {
  brand: "var(--color-brand)",
  success: "var(--color-success)",
  accent: "var(--color-accent)",
  warning: "var(--color-warning)",
  danger: "var(--color-danger)",
  info: "var(--color-info)",
};

/**
 * The categorical chart slot each tint maps to.
 *
 * The bridge between the two systems, for full-size charts where the validated
 * palette's adjacent-pair guarantees do matter. A tile tinted `accent` and the
 * revenue chart below it therefore agree on magenta.
 *
 * `danger` has no slot of its own — the validated ramp is five long, and a
 * series meaning "bad" should be carrying a label rather than relying on red.
 * Single-series marks should use `TINT_COLOR` instead.
 */
export const TINT_CHART_SLOT: Record<Tint, number> = {
  brand: 0,
  warning: 1,
  accent: 2,
  success: 3,
  info: 4,
  danger: 2,
};
